import { inject, Injectable } from '@angular/core';
import { UrlTree } from '@angular/router';

import { StorageEnum } from '@app/shared/enums/storage.enum';

import { logAuthPortal, logAuthPortalError } from './auth-portal.logger';
import { AuthSessionHydrationService } from './auth-session-hydration.service';
import { DevLocalAuthFlowService } from './dev-local-auth-flow.service';
import { isDevLocalBypassMode } from './utils';
import { PortalTokenPayload } from '../models/portal-token-payload.model';
import { encryptObfuscated } from '../security/state-cypher';
import { AUTH_STORAGE_KEYS } from '../services/api/auth.constants';
import {
  AuthService,
  hasPortalUserId,
  portalUserIdsMatch
} from '../services/api/auth.service';
import { AuthSessionService, Session } from '../services/auth-session.service';
import { StorageService } from '../services/storage.service';

/**
 * Orquestra o bootstrap de autenticação na entrada da app e nos guards:
 * hidrata sessão (usuário, escolas, termos), login por URL/token persistido e modo dev local.
 *
 * Delegações:
 * - {@link AuthSessionHydrationService} — whoAmI, escolas, termos, retries.
 * - {@link DevLocalAuthFlowService} — login interno (`/internal-login`) + skip único de hidratação na raiz.
 */
@Injectable({ providedIn: 'root' })
export class AuthInitService {
  private readonly apiAuth = inject(AuthService);
  private readonly hydration = inject(AuthSessionHydrationService);
  private readonly devFlow = inject(DevLocalAuthFlowService);
  private readonly sessionStore = inject(AuthSessionService);
  private readonly storage = inject(StorageService);

  /**
   * Executado pelo {@link authInitGuard}: com token presente, hidrata sessão e termos.
   * Sem token em modo `?mode=dev`, devolve `true` sem chamar API (evita 401 na 1ª pintura).
   */
  async initialize(navigationTargetUrl?: string): Promise<boolean | UrlTree> {
    await this.handlePortalEntryWithExistingSession();

    const token = this.hydration.getStoredOrSnapshotAccessToken();

    if (!token) {
      return isDevLocalBypassMode();
    }

    if (this.devFlow.consumeSkipNextRootHydrationIfScheduled()) {
      this.processUrlTokenFromQuery();
      return true;
    }

    this.processUrlTokenFromQuery();

    try {
      const redirect =
        await this.hydration.hydrateAuthenticatedSession(navigationTargetUrl);
      if (redirect) {
        return redirect;
      }
      return true;
    } catch (error) {
      logAuthPortalError('Hydration failed', error);
      this.handleInitializationFailure(error);
      return false;
    }
  }

  /**
   * Chamado pelo auth guard quando não há sessão em localhost com `?mode=dev`.
   */
  async ensureDevLoggedIn(): Promise<boolean | UrlTree> {
    return this.devFlow.ensureDevLoggedIn();
  }

  /**
   * Login “produção” quando não está em {@link isDevLocalBypassMode}
   * (sem `?mode=dev` em localhost = mesmo fluxo que ambiente real).
   */
  async ensureProductionLoggedIn(
    forceUrlToken = false,
    forcedToken?: string
  ): Promise<boolean> {
    try {
      return await this.runProductionLogin(forceUrlToken, forcedToken);
    } catch (error) {
      logAuthPortalError('Login failed', error);
      this.handleInitializationFailure(error);
      return false;
    }
  }

  private async runProductionLogin(
    forceUrlToken: boolean,
    forcedToken?: string
  ): Promise<boolean> {
    const storedSession =
      this.storage.get<Session>(StorageEnum.Session) ?? null;
    const storageToken = storedSession?.accessToken ?? null;
    const storageUserId = storedSession?.user?.id;
    const portalToken = this.apiAuth.getPortalTokenFromUrl();
    const accessToken = forcedToken ?? portalToken?.accessToken ?? null;

    if (forceUrlToken && accessToken) {
      this.apiAuth.stashRawPortalTokenFromUrlIfPresent();
      return this.hydration.establishSessionFromUrlToken(accessToken);
    }

    const sameUserHandled = await this.tryProductionLoginPreferringStorage(
      portalToken,
      accessToken,
      storageUserId,
      !!storageToken
    );
    if (sameUserHandled !== null) {
      return sameUserHandled;
    }

    const differentUserHandled = await this.tryProductionLoginWithPortalToken(
      portalToken,
      accessToken,
      storageUserId
    );
    if (differentUserHandled !== null) {
      return differentUserHandled;
    }

    return this.tryProductionLoginFromStorageOrPortal(
      storageToken,
      accessToken
    );
  }

  private async tryProductionLoginPreferringStorage(
    portalToken: PortalTokenPayload | null,
    accessToken: string | null,
    storageUserId: number | undefined,
    hasStorageToken: boolean
  ): Promise<boolean | null> {
    if (
      !portalToken ||
      !accessToken ||
      !this.shouldPreferStorageSession(
        portalToken,
        storageUserId,
        hasStorageToken
      )
    ) {
      return null;
    }

    logAuthPortal('Same userId — validating storage session', {
      sessionUserId: storageUserId,
      portalUserId: portalToken.userId
    });
    this.apiAuth.clearTokenFromUrl();
    try {
      const ok = await this.hydration.establishSessionFromStoredWhoAmI();
      this.apiAuth.clearStashedUrlToken();
      return ok;
    } catch {
      logAuthPortal('Storage session invalid — portal token fallback');
      this.apiAuth.stashRawPortalTokenFromUrlIfPresent();
      return this.hydration.establishSessionFromUrlToken(accessToken);
    }
  }

  private async tryProductionLoginWithPortalToken(
    portalToken: PortalTokenPayload | null,
    accessToken: string | null,
    storageUserId: number | undefined
  ): Promise<boolean | null> {
    const hasPortalCredentials = !!portalToken && !!accessToken;
    const portalUserIdentified = !!portalToken && hasPortalUserId(portalToken);
    const hasStoredUserId = storageUserId !== undefined;
    const isSameUserAsStorage =
      portalUserIdentified &&
      hasStoredUserId &&
      portalUserIdsMatch(portalToken.userId, storageUserId);

    if (
      !hasPortalCredentials ||
      !portalUserIdentified ||
      !hasStoredUserId ||
      isSameUserAsStorage
    ) {
      return null;
    }

    logAuthPortal('Different userId — portal login', {
      sessionUserId: storageUserId,
      portalUserId: portalToken.userId
    });
    this.apiAuth.stashRawPortalTokenFromUrlIfPresent();
    return this.hydration.establishSessionFromUrlToken(accessToken);
  }

  private async tryProductionLoginFromStorageOrPortal(
    storageToken: string | null,
    accessToken: string | null
  ): Promise<boolean> {
    if (storageToken) {
      try {
        return await this.hydration.establishSessionFromStoredWhoAmI();
      } catch (err) {
        if (accessToken) {
          logAuthPortal('Storage session invalid — portal token fallback');
          this.apiAuth.stashRawPortalTokenFromUrlIfPresent();
          return this.hydration.establishSessionFromUrlToken(accessToken);
        }
        throw err;
      }
    }

    if (accessToken) {
      this.apiAuth.stashRawPortalTokenFromUrlIfPresent();
      return this.hydration.establishSessionFromUrlToken(accessToken);
    }

    return false;
  }

  /**
   * Já autenticado + token do portal (URL ou stash após {@link stripTokenGuard}).
   * Mesmo userId → valida sessão guardada ou renova com token do portal.
   * Outro userId → substitui sessão via login por URL.
   */
  private async handlePortalEntryWithExistingSession(): Promise<void> {
    const incoming = this.apiAuth.resolvePortalEntryToken();
    if (!incoming || !this.hydration.getStoredOrSnapshotAccessToken()) {
      return;
    }

    try {
      await this.applyPortalEntryWithExistingSession(incoming);
    } catch (error) {
      logAuthPortalError('Session switch failed', error);
    } finally {
      this.apiAuth.clearTokenFromUrl();
    }
  }

  private async applyPortalEntryWithExistingSession(
    incoming: PortalTokenPayload
  ): Promise<void> {
    const sessionUserId = this.resolveSessionUserId();
    const hasStorageToken = !!this.hydration.getStoredOrSnapshotAccessToken();

    if (
      this.shouldPreferStorageSession(incoming, sessionUserId, hasStorageToken)
    ) {
      logAuthPortal('Same userId — validating storage session', {
        sessionUserId,
        portalUserId: incoming.userId
      });
      try {
        await this.hydration.establishSessionFromStoredWhoAmI();
        this.apiAuth.clearStashedUrlToken();
        return;
      } catch {
        logAuthPortal('Storage session invalid — portal token fallback');
        await this.hydration.establishSessionFromUrlToken(incoming.accessToken);
        this.apiAuth.clearStashedUrlToken();
        return;
      }
    }

    if (!hasPortalUserId(incoming)) {
      logAuthPortal('Portal userId missing — keeping storage session', {
        sessionUserId
      });
      this.apiAuth.clearStashedUrlToken();
      return;
    }

    logAuthPortal('Different userId — portal login', {
      sessionUserId,
      portalUserId: incoming.userId
    });
    await this.hydration.establishSessionFromUrlToken(incoming.accessToken);
    this.apiAuth.clearStashedUrlToken();
  }

  private resolveSessionUserId(): number | undefined {
    const currentUserId = this.sessionStore.snapshot.user?.id;
    const storageUserId = this.storage.get<Session>(StorageEnum.Session)?.user
      ?.id;
    return currentUserId ?? storageUserId;
  }

  private shouldPreferStorageSession(
    portalToken: PortalTokenPayload,
    storageUserId: number | undefined,
    hasStorageToken: boolean
  ): boolean {
    return (
      !!hasStorageToken &&
      hasPortalUserId(portalToken) &&
      storageUserId !== undefined &&
      portalUserIdsMatch(portalToken.userId, storageUserId)
    );
  }

  /**
   * Com `?token=` na URL: em modo dev local guarda para fallback 401; sempre remove da URL.
   */
  private processUrlTokenFromQuery(): void {
    const portalToken = this.apiAuth.getPortalTokenFromUrl();
    if (!portalToken) {
      return;
    }
    if (isDevLocalBypassMode()) {
      const encryptedToken = encodeURIComponent(encryptObfuscated(portalToken));
      this.storage.save(
        AUTH_STORAGE_KEYS.STASHED_URL_TOKEN,
        encryptedToken,
        'session'
      );
    }
    this.apiAuth.clearTokenFromUrl();
  }

  private handleInitializationFailure(error: unknown): void {
    logAuthPortalError('Initialization failed', error);
    this.hydration.clearSessionIfAccessTokenPresent();
  }
}
